import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import {
  createColumn,
  formatDateTime,
  calculateDuration,
  createViewAction,
} from "../../utils/helpers";
import { URL_PARKED_CARS } from "../../utils/urls";
import useGet from "../common/useGet";
import { API_PARKINGS } from "../../utils/apis";
import { StatusTag } from "../../components/common/registerUtils";

// Helper function to resolve parking name by ID
const resolveParkingName = (parkingId: any, parkingsData: any) => {
  if (!parkingId || !parkingsData) {
    return "Unknown Parking";
  }

  // Handle both array and object with results property
  const parkingsArray = Array.isArray(parkingsData)
    ? parkingsData
    : parkingsData?.results || [];

  if (!Array.isArray(parkingsArray) || parkingsArray.length === 0) {
    return "Loading...";
  }

  const parking = parkingsArray.find((p) => p.id === parkingId);
  return parking ? parking.name : "Unknown Parking";
};

export const useParkedCars = () => {
  // Get parkings data
  const {
    loadData: loadParkingData,
    loading: loadingParkings,
    data: parkingsData,
  } = useGet();

  useEffect(() => {
    loadParkingData({ api: API_PARKINGS });
  }, []);

  const tableColumns = () => [
    createColumn("car_number", true, "Number Plate"),
    createColumn("phone_number", true),
    createColumn("parking_name", true),
    createColumn("district", true),
    createColumn("slot_name", true),
    createColumn("vehicle_type", true),
    createColumn("car_type", true),
    createColumn("start_time", { type: "date" }),
    createColumn("end_time", { type: "date" }),
    createColumn("duration", false),
    createColumn("status", false),
    createColumn("actions", false),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      // Check if parking is actually ended and has an end_time
      const isActuallyEnded = item.is_parking_ended && item.end_time;

      const duration = isActuallyEnded
        ? calculateDuration(item.start_time, item.end_time)
        : calculateDuration(item.start_time, new Date().toISOString());

      return {
        ...item,
        start_time: formatDateTime(item.start_time),
        end_time: isActuallyEnded ? formatDateTime(item.end_time) : "Active",
        duration: duration,
        status: isActuallyEnded ? (
          <StatusTag status="ended" />
        ) : (
          <StatusTag status="active" />
        ),
        // parking_name: resolveParkingName(item.parking, parkingsData),
        parking_name: item?.parking_name,
        slot_name: item?.slot_name,
        vehicle_type: item?.vehicle_type_name,
        car_type: item?.car_type_name,
        actions: (
          <FormActions
            actions={[createViewAction(URL_PARKED_CARS, item?.id)]}
          />
        ),
      };
    });
  };

  const viewData = (item: any) => {
    // Handle case where item is undefined or null
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }

    // Check if parking is actually ended and has an end_time
    const isActuallyEnded = item.is_parking_ended && item.end_time;

    const duration = isActuallyEnded
      ? calculateDuration(item.start_time, item.end_time)
      : item.start_time
        ? calculateDuration(item.start_time, new Date().toISOString())
        : "N/A";

    return {
      sections: [
        {
          title: "Vehicle Information",
          fields: {
            car_number: item?.car_number || "N/A",
            phone_number: item?.phone_number || "N/A",
            email: item?.email || "Not provided",
            car_size: item?.car_size_name || "Not specified",
            car_type: item?.car_type_name || "Not specified",
          },
        },
        {
          title: "Parking Details",
          fields: {
            parking_location: resolveParkingName(item?.parking, parkingsData),
            // parking_slot: resolveSlotName(item?.slot_name, slotsData),
            slot_name: item?.slot_name,
            status: isActuallyEnded ? "Ended" : "Active",
            declaration: item?.declaration ? "Accepted" : "Not accepted",
          },
        },
        {
          title: "Time Information",
          fields: {
            start_time: item?.start_time
              ? formatDateTime(item.start_time)
              : "N/A",
            end_time: isActuallyEnded
              ? formatDateTime(item.end_time)
              : "Still active",
            duration: duration,
            parking_status: item?.is_parking_ended
              ? "Marked as ended"
              : "Active session",
          },
        },
        {
          title: "Location Coordinates",
          fields: {
            latitude: item?.latitude || "0",
            longitude: item?.longitude || "0",
          },
        },
        {
          title: "System Information",
          fields: {
            record_id: item?.id || "N/A",
            created_by: item?.created_by
              ? `${item.created_by.first_name} ${item.created_by.last_name}`
              : "System",
            created_at: item?.created_at
              ? formatDateTime(item.created_at)
              : "N/A",
            updated_at: item?.updated_at
              ? formatDateTime(item.updated_at)
              : "N/A",
            updated_by: item?.updated_by
              ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
              : "Not updated",
          },
        },
      ],
    };
  };

  const activityFilter = "parked_cars";

  return {
    tableData,
    tableColumns,
    viewData,
    activityFilter,
    parkingsData,
    // slotsData,
    loadingParkings,
    // loadingSlots,
  };
};
